import "./App.scss";

import { allUserRoles, cdRoles, clientRoles } from "constants/phase";
import React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";

//components
import CookieContainer from "../components/common/Cookies/container";
import TitleUpdater from "../components/common/TitleUpdater/TitleUpdater";
import { MAINTENANCE_MODE } from "../config/envConfig";
import SwitchVersion from "../containers/app/SwitchVersion/SwitchVersion";
import PreviewVideo from "../containers/preview-video";
import { SocketContext, webhookSocket } from "../context/socket";
import { addRenderingErrorToDataDogRum } from "../externals/datadog";
import ChannelSummaryComponent from "../views/ChannelSummary/container";
import Clips from "../views/Clips/container";
import CookiePolicyComponent from "../views/CookiePolicy/container";
import CustomerAgreementComponent from "../views/CustomerAgreement/container";
import ErrorBoundaryComponent from "../views/ErrorBoundry/ErrorBoundry";
import FTPcomponent from "../views/FTPpublish/container";
import OrganisationProfileComponent from "../views/OrganisationProfile/container";
import PrivacyPoliciesComponent from "../views/PrivacyPolicies/container";
import PrivateRoute from "../views/PrivateRoute/container";
import PublicRoute from "./auth/PublicRoute";
import RoleBasedRoute from "./auth/RoleBasedRoute";
import {
  AsyncAddNewVideoEditForm,
  AsyncAddNewVideoUploadingForm,
  AsyncArchiveClipsComponent,
  AsyncArchiveSettingsComponent,
  AsyncArchiveStreamComponent,
  AsyncBulkEditorComponent,
  AsyncClientProfile,
  AsyncClipRatingRule,
  AsyncClipsNewComponent,
  AsyncConfigurationComponent,
  AsyncConnectedApps,
  AsyncCopyStreamContainer,
  AsyncCropperComponent,
  AsyncDashboard,
  AsyncDataConsumed,
  AsyncEditorPage,
  AsyncEditSummaryContainer,
  AsyncEditVideoFormContainer,
  AsyncForgotPassword,
  AsyncGroupMappingDetailComponent,
  AsyncInternalStatus,
  AsyncLiveEditComponent,
  AsyncLogin,
  AsyncMyHighlightsComponent,
  AsyncMyStoriesComponent,
  AsyncOrganizationManagement,
  AsyncOverview,
  AsyncPermissionManagement,
  AsyncProcessTrackingComponent,
  AsyncPublishClipsComponent,
  AsyncPublishHistory,
  AsyncQueueProcessesComponent,
  AsyncQueueSettingComponent,
  AsyncQuickHighlights,
  AsyncReportsComponent,
  AsyncResetPassword,
  AsyncScoreBoardContainer,
  AsyncSignup,
  AsyncSingleWebstoryEditorComponent,
  AsyncSSOLogin,
  AsyncStreamDetailsContainer,
  AsyncThirdPartyContainer,
  AsyncUpdateStorageComponent,
  AsyncUserManagement,
  AsyncUserProfileBasicInfoEditComponent,
  AsyncUserProfileComponent,
  AsyncUserProfileUpdatePasswordEditComponent,
  AsyncVideoGrid,
  AsyncVideoPlayerContainer,
  AsyncVideosInfoNComponent,
  AsyncWorkspaceManagement,
} from "./LazyRoutes";
import Maintenance from "./Maintenance";
import QRHandler from "./QRHandler";

const FallbackComponent = ({ error, componentStack, resetErrorBoundary }) => {
  //function that calls Error Boundry Component with specific error
  return (
    <ErrorBoundaryComponent
      error={error}
      componentStack={componentStack}
      isOpen={true}
      resetErrorBoundary={resetErrorBoundary}
    />
  );
};

const Home = () => {
  //get url params
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const isQRCode = urlParams.get("source");
  //if req comes from qr code
  return isQRCode === "qrcode" ? <QRHandler /> : <Navigate to="/login" />;
};

function App() {
  if (MAINTENANCE_MODE === "true") {
    return (
      <ErrorBoundary FallbackComponent={FallbackComponent} onError={addRenderingErrorToDataDogRum}>
        <Maintenance />
      </ErrorBoundary>
    );
  } else {
    return (
      <ErrorBoundary FallbackComponent={FallbackComponent} onError={addRenderingErrorToDataDogRum}>
        <SocketContext.Provider value={{ webhookSocket }}>
          <Router>
            <SwitchVersion />
            <Routes>
              <Route
                element={
                  <>
                    <CookieContainer />
                    <TitleUpdater />
                  </>
                }
              >
                {/** Public Routes */}
                <Route exact path="/" element={<Home />} />
                <Route exact path="/preview-video" element={<PreviewVideo />} />
                <Route exact path="/sso-login" element={<PublicRoute element={AsyncSSOLogin} />} />
                <Route exact path="/login" element={<PublicRoute element={AsyncLogin} />} />
                <Route exact path="/forgot-password" element={<PublicRoute element={AsyncForgotPassword} />} />
                <Route exact path="/signup" element={<PublicRoute element={AsyncSignup} />} />
                <Route exact path="/reset/:token" element={<PublicRoute element={AsyncResetPassword} />} />
                <Route exact path="/customer-agreement" element={<CustomerAgreementComponent />} />
                <Route exact path="/privacy-policies" element={<PrivacyPoliciesComponent />} />
                <Route exact path="/cookie-policy" element={<CookiePolicyComponent />} />
                <Route
                  exact
                  path="/update-password-edit/:id"
                  element={<AsyncUserProfileUpdatePasswordEditComponent />}
                />
                <Route element={<PrivateRoute />}>
                  <Route exact path="/channel-summary" element={<ChannelSummaryComponent />} />
                  {/** Private Routes */}
                  <Route
                    exact
                    path="/organizations-profile/:id/:active_tab"
                    element={<OrganisationProfileComponent />}
                  />
                  <Route exact path="/workspaces-profile/:id/:active_tab" element={<OrganisationProfileComponent />} />
                  <Route
                    exact
                    path="/dashboard"
                    element={
                      <RoleBasedRoute
                        element={AsyncDashboard}
                        allowedRoles={[...Object.values(clientRoles), ...Object.values(cdRoles)]}
                      />
                    }
                  />
                  <Route
                    exact
                    path="/overview"
                    element={
                      <RoleBasedRoute
                        element={AsyncOverview}
                        allowedRoles={[allUserRoles.superAdmin, allUserRoles.admin, ...Object.values(cdRoles)]}
                      />
                    }
                  />
                  <Route
                    exact
                    path="/user-management"
                    element={
                      <RoleBasedRoute
                        element={<AsyncUserManagement opsView={false} />}
                        allowedRoles={[allUserRoles.superAdmin]}
                      />
                    }
                  />
                  <Route
                    exact
                    path="/ops-management"
                    element={
                      <RoleBasedRoute
                        element={<AsyncUserManagement opsView={true} />}
                        allowedRoles={[allUserRoles.superAdmin]}
                      />
                    }
                  />
                  <Route
                    exact
                    path="/organization-management"
                    element={
                      <RoleBasedRoute element={AsyncOrganizationManagement} allowedRoles={[allUserRoles.superAdmin]} />
                    }
                  />
                  <Route
                    exact
                    path="/workspace-management"
                    element={
                      <RoleBasedRoute element={AsyncWorkspaceManagement} allowedRoles={[allUserRoles.superAdmin]} />
                    }
                  />
                  <Route exact path="/organization-profile/:id" element={AsyncClientProfile} />
                  <Route exact path="/permission-management" element={<AsyncPermissionManagement />} />
                  <Route exact path="/configuration/:active_tab" element={<AsyncConfigurationComponent />} />
                  <Route exact path="/user-profile/:id" element={<AsyncUserProfileComponent />} />
                  <Route exact path="/basic-info-edit/:id" element={<AsyncUserProfileBasicInfoEditComponent />} />
                  <Route exact path="/dashboard-consumption-calculator-list" element={<AsyncDataConsumed />} />
                  <Route exact path="/videos" element={<AsyncVideoGrid />} />
                  <Route exact path="/clips/:id" element={<Clips />} />
                  <Route exact path="/add-video" element={<AsyncAddNewVideoUploadingForm />} />
                  <Route exact path="/archive-streams" element={<AsyncArchiveStreamComponent />} />
                  <Route exact path="/edit-video/:id" element={<AsyncEditVideoFormContainer />} />
                  <Route exact path="/edit-clips/:id" element={<AsyncEditSummaryContainer />} />
                  <Route exact path="/archive-entities/:id" element={<AsyncArchiveClipsComponent />} />
                  <Route exact path="/copy-stream" element={<AsyncCopyStreamContainer />} />
                  <Route exact path="/video-player" element={<AsyncVideoPlayerContainer />} />
                  <Route exact path="/social-media/share" element={<AsyncThirdPartyContainer />} />
                  <Route exact path="/connected-apps" element={<AsyncConnectedApps />} />
                  <Route exact path="/configuration/:active_tab" element={<AsyncConfigurationComponent />} />
                  <Route exact path="/reports" element={<AsyncReportsComponent />} />
                  <Route exact path="/update-storage/" element={<AsyncUpdateStorageComponent />} />
                  <Route exact path="/highlights/:id" element={<AsyncMyHighlightsComponent />} />
                  <Route exact path="/stories/:id" element={<AsyncMyStoriesComponent />} />
                  <Route exact path="/clip-publish/:id" element={<AsyncPublishClipsComponent />} />
                  <Route exact path="/live-video/:id" element={<AsyncClipsNewComponent />} />
                  <Route exact path="/live-edit/:id" element={<AsyncLiveEditComponent />} />
                  <Route exact path="/group-mapping-key/:mappingKey" element={<AsyncGroupMappingDetailComponent />} />
                  <Route exact path="/editor-page/:id" element={<AsyncEditorPage />} />
                  <Route exact path="/clip-info/:clipID" element={<AsyncVideosInfoNComponent />} />
                  <Route exact path="/bulk-edits" element={<AsyncBulkEditorComponent />} />
                  <Route exact path="/client-archive" element={<AsyncArchiveSettingsComponent />} />
                  <Route exact path="/clip-info/scoreboard/:clipID" element={<AsyncScoreBoardContainer />} />
                  <Route exact path="/single-webstory-editor/:id" element={<AsyncSingleWebstoryEditorComponent />} />
                  <Route exact path="/stream-details/:id" element={<AsyncStreamDetailsContainer />} />
                  <Route exact path="/quick-highlight/" element={<AsyncEditorPage />} />
                  <Route exact path="/quick-highlight/:id" element={<AsyncEditorPage />} />
                  <Route exact path="/my-highlights" element={<AsyncQuickHighlights />} />
                  <Route exact path="/ftp-publish/:id" element={<FTPcomponent />} />
                  <Route exact path="/cropper/:clipId" element={<AsyncCropperComponent />} />
                  <Route exact path="/queue-setting" element={<AsyncQueueSettingComponent />} />
                  <Route exact path="/vod-queue-setting" element={<AsyncQueueSettingComponent />} />
                  <Route exact path="/process-tracking" element={<AsyncProcessTrackingComponent />} />
                  <Route exact path="/queue-processes" element={<AsyncQueueProcessesComponent />} />
                  <Route exact path="/add-video/:id" element={<AsyncAddNewVideoEditForm />} />
                  <Route exact path="/publish-history" element={<AsyncPublishHistory />} />
                  <Route exact path="/internal-status" element={<AsyncInternalStatus />} />
                  <Route exact path="/clip-rating-rule" element={<AsyncClipRatingRule />} />
                  <Route exact path="/permission-management" element={<AsyncPermissionManagement />} />
                </Route>
                {/* Catch-all route to redirect to the dashboard */}
                <Route path="*" element={<Navigate to="/dashboard" />} />
              </Route>
            </Routes>
          </Router>
        </SocketContext.Provider>
      </ErrorBoundary>
    );
  }
}

export default App;
